const platforms = [
  {
    name: 'Facebook Ads',
    icon: 'ri-facebook-circle-fill',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    border: 'border-sky-100',
    format: '1200×628px image · 125 char primary text · 40 char headline',
    adTypes: ['Single Image', 'Carousel', 'Lead Form', 'Traffic', 'Conversion'],
    tip: 'Facebook rewards social proof and emotional storytelling. AdForge leads with a relatable hook before the offer.',
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/d4bdda11-3c1d-4721-ad83-ca816c392ea1_ChatGPT-Image-Mar-27-2026-11_16_58-AM.png?v=c4304fc1ef3a97c0d1dc25b4950a2d2c',
  },
  {
    name: 'Instagram Ads',
    icon: 'ri-instagram-line',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-100',
    format: '1080×1080px square · 2,200 char caption · 30 hashtags max',
    adTypes: ['Feed Post', 'Stories', 'Reels', 'Explore', 'Shopping'],
    tip: 'Instagram is visual-first. AdForge generates square-format creatives with minimal text overlay and punchy captions.',
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/7f154710-1e89-4ac9-9d2c-8d94f584eba5_ChatGPT-Image-Mar-27-2026-11_20_32-AM.png?v=2b68e06b414e6db291ca41070bd4126d',
  },
  {
    name: 'Google Ads',
    icon: 'ri-google-fill',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    format: '30 char headline × 3 · 90 char description × 2 · keyword-rich',
    adTypes: ['Search', 'Display', 'Performance Max', 'Shopping', 'Local'],
    tip: 'Google Ads need keyword-rich, benefit-forward copy. AdForge structures headlines to match search intent and drive clicks.',
    image: 'https://readdy.ai/api/search-image?query=laptop%20screen%20showing%20a%20clean%20Google%20search%20results%20page%20with%20a%20sponsored%20ad%20at%20the%20top%20featuring%20readable%20English%20headline%20text%20reading%20Best%20Price%20Guaranteed%20Free%20Shipping%20and%20description%20text%20Save%20Up%20To%2040%20Percent%20Shop%20Top%20Brands%20Today%2C%20realistic%20browser%20chrome%2C%20white%20background%2C%20crisp%20sharp%20screen&width=480&height=300&seq=platform-google-v3&orientation=landscape',
  },
  {
    name: 'TikTok Ads',
    icon: 'ri-tiktok-fill',
    color: 'text-stone-800',
    bg: 'bg-stone-50',
    border: 'border-stone-100',
    format: '9:16 vertical video cover · 80 char text · trending hook style',
    adTypes: ['TopView', 'In-Feed', 'Branded', 'Spark Ads', 'Collection'],
    tip: 'TikTok copy needs to be fast, conversational, and native. AdForge writes short punchy scripts designed for vertical video.',
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/38c13a40-056b-44ce-a950-185c383cc4ca_ChatGPT-Image-Mar-27-2026-11_26_55-AM.png?v=c167bd956637ec2c38560c28cbe60886',
  },
  {
    name: 'LinkedIn Ads',
    icon: 'ri-linkedin-box-fill',
    color: 'text-sky-700',
    bg: 'bg-sky-50',
    border: 'border-sky-100',
    format: '1200×627px image · 150 char intro text · 70 char headline',
    adTypes: ['Sponsored Content', 'Message Ads', 'Dynamic Ads', 'Lead Gen'],
    tip: 'LinkedIn audiences expect professionalism and data. AdForge writes B2B-appropriate copy with authority signals and ROI focus.',
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/8c5c9a86-550a-4431-8842-da6eece364af_ChatGPT-Image-Mar-27-2026-11_23_22-AM.png?v=3ee6f0df086426ee82aeb4b5ea86e197',
  },
];

export default function HIWPlatforms() {
  return (
    <section className="py-20 lg:py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">Platform Guide</span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            How the AI Ad Generator Works for Each Platform
          </h2>
          <p className="text-stone-500 text-base leading-relaxed">
            Every platform has different formats, audiences, and rules. AdForge automatically
            adapts your ad to the platform you choose — here&apos;s exactly what that looks like.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {platforms.map((p) => (
            <article
              key={p.name}
              className={`bg-white rounded-2xl border ${p.border} overflow-hidden hover:-translate-y-0.5 transition-transform duration-200 cursor-default`}
            >
              {/* Platform image */}
              <div className="w-full h-40 overflow-hidden">
                <img
                  src={p.image}
                  alt={`How to create ${p.name} ads with AI`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="p-5">
                {/* Header */}
                <div className="flex items-center gap-2.5 mb-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${p.bg}`}>
                    <i className={`${p.icon} text-lg ${p.color}`} />
                  </div>
                  <h3 className="text-sm font-bold text-stone-900">{p.name}</h3>
                </div>

                {/* Format */}
                <p className="text-xs text-stone-400 font-mono bg-stone-50 rounded-lg px-3 py-2 mb-3 leading-relaxed">
                  {p.format}
                </p>

                {/* Ad types */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {p.adTypes.map((type) => (
                    <span
                      key={type}
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${p.bg} ${p.color}`}
                    >
                      {type}
                    </span>
                  ))}
                </div>

                {/* Tip */}
                <div className="flex items-start gap-2 bg-stone-50 rounded-xl p-3">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-flash-line text-orange-400 text-sm" />
                  </div>
                  <p className="text-xs text-stone-500 leading-relaxed">{p.tip}</p>
                </div>
              </div>
            </article>
          ))}

          {/* Last card — CTA */}
          <article className="bg-orange-500 rounded-2xl p-6 flex flex-col justify-between">
            <div>
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mb-4">
                <i className="ri-add-circle-line text-white text-xl" />
              </div>
              <h3 className="text-white font-black text-lg mb-2 leading-snug">More Platforms Coming</h3>
              <p className="text-orange-100 text-sm leading-relaxed mb-6">
                Pinterest, YouTube, Snapchat, and X (Twitter) ad formats are on the roadmap. All existing plans will get access automatically.
              </p>
            </div>
            <div className="text-xs font-semibold text-orange-200">
              Already on 5 major platforms →
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
